import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, timeAgo } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StatusChip } from "../components/Artifacts";
import { AlertTriangle, CheckSquare, Gavel, ChevronRight, AtSign } from "lucide-react";

export default function ActivityPage() {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [act, men] = await Promise.all([
      api.get(`/activity?user_id=${user.id}`),
      api.get(`/mentions?user_id=${user.id}`).catch(() => ({ data: [] })),
    ]);
    setItems(act.data);
    setMentions(men.data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? items : items.filter((i) => i.kind === filter);
  const unseenMentions = mentions.filter((m) => !m.seen).length;

  const iconFor = { issue: AlertTriangle, task: CheckSquare, decision: Gavel };
  const colorFor = { issue: "text-destructive bg-destructive/10", task: "text-accent-foreground bg-accent/20", decision: "text-[hsl(var(--success))] bg-[hsl(var(--success))]/10" };

  const onClickItem = (item) => {
    // Prefer jumping straight to the source message in the conversation
    if (item.conversation_id && item.message_id) {
      navigate(`/chat/${item.conversation_id}?msg=${item.message_id}`);
      return;
    }
    if (item.kind === "issue" && item.job) navigate(`/jobs/${item.job.id}?tab=issues`);
    else if (item.kind === "task" && item.job) navigate(`/jobs/${item.job.id}?tab=tasks`);
    else if (item.kind === "decision" && item.conversation_id) navigate(`/chat/${item.conversation_id}`);
    else if (item.job) navigate(`/jobs/${item.job.id}`);
  };

  return (
    <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6">
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">{t("activity")}</h1>
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max">
            <TabsTrigger value="all" data-testid="activity-filter-all">{t("all")}</TabsTrigger>
          <TabsTrigger value="issue" data-testid="activity-filter-issues">{t("issues")}</TabsTrigger>
          <TabsTrigger value="task" data-testid="activity-filter-tasks">{t("tasks")}</TabsTrigger>
          <TabsTrigger value="decision" data-testid="activity-filter-decisions">{t("decisions")}</TabsTrigger>
          <TabsTrigger value="mentions" data-testid="activity-filter-mentions">
            {t("mentions")}
            {unseenMentions > 0 && (
              <Badge data-testid="mentions-unseen-badge" className="ml-1.5 bg-[hsl(var(--accent))] text-accent-foreground text-[9px] px-1.5 min-w-[18px] h-4 justify-center">
                {unseenMentions}
              </Badge>
            )}
          </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* Mentions tab: every time someone mentioned you, with a shortcut to the message */}
      {filter === "mentions" ? (
        <div className="space-y-2" data-testid="mentions-list">
          {loading && [1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}
          {!loading && mentions.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">{t("no_mentions")}</div>}
          {!loading && mentions.map((m) => {
            const trContent = (m.translations?.[lang]?.content || "").trim();
            return (
              <Card
                key={m.message_id}
                data-testid="mention-row"
                onClick={() => navigate(`/chat/${m.conversation_id}?msg=${m.message_id}`)}
                className={`p-3.5 cursor-pointer hover:bg-muted/40 transition-colors flex items-center gap-3 ${!m.seen ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/5" : ""}`}
              >
                <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15">
                  <AtSign className="h-4.5 w-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate">{m.sender_name}</span>
                    <span className="text-xs text-muted-foreground truncate">· {m.conversation_name}</span>
                    {!m.seen && (
                      <Badge className="bg-[hsl(var(--accent))] text-accent-foreground text-[9px]" data-testid="mention-new-badge">{t("new")}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{trContent || m.content}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-xs text-muted-foreground">
                  {timeAgo(m.created_at, lang)}
                  <ChevronRight className="h-4 w-4" />
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
      <div className="space-y-2" data-testid="activity-list">
        {loading && [1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}
        {!loading && filtered.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">{t("no_activity")}</div>}
        {!loading && filtered.map((item, idx) => {
          const Icon = iconFor[item.kind] || AlertTriangle;
          return (
            <Card
              key={`${item.kind}-${item.ref_id}-${idx}`}
              data-testid="activity-row"
              onClick={() => onClickItem(item)}
              className="p-3.5 cursor-pointer hover:bg-muted/40 transition-colors flex items-center gap-3"
            >
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${colorFor[item.kind]}`}>
                <Icon className="h-4.5 w-4.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{item.title}</span>
                  {item.kind === "issue" && <StatusChip status={item.status} t={t} />}
                  {item.kind === "task" && (
                    <Badge className={item.status === "done" ? "bg-[hsl(var(--success))] text-white text-[9px]" : "bg-accent text-accent-foreground text-[9px]"}>
                      {item.status === "done" ? t("done") : t("status_open")}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {item.job && <span className="font-mono-plex font-semibold text-primary">#{item.job.job_number}</span>}
                  {item.job && ` · ${item.job.customer_name} · `}
                  {item.by_name}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 text-xs text-muted-foreground">
                {timeAgo(item.at, lang)}
                <ChevronRight className="h-4 w-4" />
              </div>
            </Card>
          );
        })}
      </div>
      )}
    </div>
  );
}
